'use client';

import { ChevronLeftIcon } from '@heroicons/react/outline';
import { ChevronRightIcon, PlusIcon } from '@heroicons/react/solid';
import { Dropdown, Input, Menu, Tooltip } from 'antd';
import { Button } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import type React from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { user } from '../../atoms/user';
import FriendInvited, {
  InvitedFriend,
} from '../../components/ReferEarnComp/FriendInvited';
import FriendsJoinedTable, {
  type Friend,
} from '../../components/ReferEarnComp/FriendsJoined';
import ButtonDefault, {
  ButtonVariants,
} from '../../components/shared/basic/button';
import { pageTitle } from '../../components/shared/basic/customHead';
import { ShouldRender } from '../../components/shared/basic/ShouldRender';
import { formateErrorObject } from '../../components/shared/formateErrorObject';
import { notify } from '../../components/shared/notify';
import { ISendFeedbackType, sendFeedback } from '../../services/ContactUs';
import {
  getInvitedUser,
  getInviteFriend,
  getRewardRulesByName,
} from '../../services/reward';
import type { User } from '../../utils/interfaces/User';
import {
  BoxGift,
  coinFill,
  Facebook,
  GiftBox,
  hands,
  shareForwardFill,
  userFollowFill,
} from '../../../assets';
import CopyIcon from '../../../assets/Icons/CopyIcon';
import LinkedInIcon from '../../../assets/Icons/LinkedInIcon';
import WhatsAppIcon from '../../../assets/Icons/WhatsAppIcon';

const IconMap: Record<'gift' | 'coin' | 'share' | 'user', any> = {
  gift: BoxGift,
  coin: coinFill,
  share: shareForwardFill,
  user: userFollowFill,
};

const REFERRAL_STEPS = [
  {
    icon: 'share',
    text: 'You share your referral link with friends.',
  },
  {
    icon: 'user',
    text: 'Your friends sign up on the app and verify their email.',
  },
  {
    icon: 'gift',
    text: 'You and your friends will be rewarded with 100 Z coins.',
  },
  {
    icon: 'coin',
    text: 'Redeem products by using Z-coins.',
  },
];

const dashboardCards = [
  {
    id: 1,
    title: 'VIEW FREE PRODUCTS',
    value: '',
    subtitle: '',
    icon: GiftBox,
    iconColor: 'text-primary',
    iconBg: 'bg-primary',
    textColor: 'text-primary',
    showArrow: false,
  },
  {
    id: 2,
    title: '01',
    subtitle: 'Total Joined',
    icon: userFollowFill,
    iconColor: 'text-zinc-400',
    iconBg: 'bg-zinc-900',
    textColor: 'text-white',
    showArrow: true,
  },
  // {
  //   id: 3,
  //   title: '04',
  //   subtitle: 'Total Invited',
  //   icon: userSharedFill,
  //   iconColor: 'text-zinc-400',
  //   iconBg: 'bg-zinc-900',
  //   textColor: 'text-white',
  //   showArrow: true,
  // },
];

export interface FAQItem {
  question: string;
  answer: string;
}

const ReferralStepIcon = ({ icon }: { icon: string }) => (
  <motion.div
    className='w-10 h-10 rounded-full bg-[#282829] p-2 flex items-center justify-center'
    initial={{ scale: 0.8, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ type: 'spring', damping: 15 }}
  >
    {icon && (
      <Image
        src={IconMap[icon as keyof typeof IconMap] || '/placeholder.svg'}
        alt={icon}
        width={24}
        height={24}
        style={{ objectFit: 'contain' }}
      />
    )}
  </motion.div>
);

const ReferEarn = () => {
  const [tooltipText, setTooltipText] = useState('Copy');
  const [showTooltip, setShowTooltip] = useState(false);
  const [coins, setCoins] = useState(0);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [feedbackInput, setFeedbackInput] = useState<string>('');
  const [animationComplete, setAnimationComplete] = useState({
    header: false,
    referralLink: false,
    cards: false,
    howItWorks: false,
    faq: false,
    tables: false,
    feedback: false,
    clientReady: false,
    cardsWeb: false,
  });
  const [loading, setLoading] = useState(false);
  const [loadingFriendsTable, setLoadingFriendsTable] = useState(false);
  const [loadingInvitedTable, setLoadingInvitedTable] = useState(false);

  const [data, setData] = useState<Friend[]>([]);
  const [invitedData, setInvitedData] = useState<InvitedFriend[]>([]);

  const [isMobile, setIsMobile] = useState(false);

  // New state to control which view is displayed
  const [currentView, setCurrentView] = useState<
    'main' | 'friendsJoined' | 'friendInvited'
  >('main');

  const userData: User | undefined = useRecoilValue(user);
  const router = useRouter();
  const screens = useBreakpoint();

  const headerRef = useRef<HTMLDivElement | null>(null);
  const referralLinkRef = useRef<HTMLElement | null>(null);
  const cardsRef = useRef<HTMLDivElement | null>(null);
  const cardsWeb = useRef<HTMLDivElement | null>(null);

  const howItWorksRef = useRef<HTMLDivElement | null>(null);
  const faqRef = useRef<HTMLDivElement | null>(null);
  const feedbackRef = useRef<HTMLDivElement | null>(null);
  const tablesRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.2,
      rootMargin: '0px 0px -10% 0px',
    };

    const observerCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const target = entry.target as HTMLElement;
          const targetId = target.id as keyof typeof animationComplete;
          setAnimationComplete((prev) => ({
            ...prev,
            [targetId]: true,
          }));
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions,
    );

    const refs = [
      { ref: headerRef, id: 'header' },
      { ref: referralLinkRef, id: 'referralLink' },
      { ref: cardsRef, id: 'cards' },
      { ref: cardsWeb, id: 'cardsWeb' },
      { ref: howItWorksRef, id: 'howItWorks' },
      { ref: faqRef, id: 'faq' },
      { ref: feedbackRef, id: 'feedback' },
      { ref: tablesRef, id: 'tables' },
    ];

    refs.forEach(({ ref, id }) => {
      if (ref.current) {
        ref.current.id = id;
        observer.observe(ref.current);
      }
    });

    return () => {
      refs.forEach(({ ref }) => {
        if (ref.current) observer.unobserve(ref.current);
      });
    };
  }, []);

  const handleSubmit = async () => {
    try {
      setLoading(true);

      await sendFeedback({
        message: feedbackInput,
        type: ISendFeedbackType.FEEDBACK,
      });
      setFeedbackInput('');

      notify('Feed back send!', 'success');
    } catch (error: any) {
      notify(formateErrorObject(error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFriend = async () => {
    try {
      setLoadingFriendsTable(true);
      const data = await getInvitedUser();
      const result = data?.data;

      setData(
        result?.map((record: any) => ({
          key: record?.id,
          name: record?.name,
          email: record?.email,
          reward: record?.wallet?.balance,
          joinDate: record.created_at,
          email_verified_at: record?.email_verified_at,
        })),
      );
    } catch (error: any) {
      const message =
        formateErrorObject(error) || 'An error occurred while fetching data.';

      notify(message, 'error');
    } finally {
      setLoadingFriendsTable(false);
    }
  };
  const handleInviteFriend = async () => {
    try {
      setLoadingInvitedTable(true);
      const data = await getInviteFriend();
      const result = data?.data;
      setInvitedData(
        result.map((record: any) => ({
          key: record.id,
          name: record.name,
          email: record.email,
          updatedAt: record.updated_at || new Date().toISOString(), // Ensure updatedAt is always provided
        })),
      );
    } catch (error: any) {
      const message =
        formateErrorObject(error) || 'An error occurred while fetching data.';

      notify(message, 'error');
    } finally {
      setLoadingInvitedTable(false);
    }
  };

  useEffect(() => {
    handleInviteFriend();
    handleFriend();
  }, []);

  const handleRewardCoin = async () => {
    try {
      const data = await getRewardRulesByName('referral_bonus');
      setCoins(data.data);
    } catch (error: any) {
      const message =
        formateErrorObject(error) || 'An error occurred while fetching data.';

      notify(message, 'error');
    }
  };

  useEffect(() => {
    handleRewardCoin();
  }, []);

  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleCopy = useCallback(() => {
    const textToCopy = `${process.env.NEXT_PUBLIC_API_URL_APP_URL}?ref_code=${userData?.ref_code}`;
    navigator.clipboard.writeText(textToCopy);
    setTooltipText('Copied!');
    setShowTooltip(true);

    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }

    tooltipTimeoutRef.current = setTimeout(() => {
      setTooltipText('Copy');
      setShowTooltip(false);
    }, 2000);
  }, [userData?.ref_code]);

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { y: 20, opacity: 0 },
    show: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24,
      },
    },
    hover: {
      y: -5,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 10,
      },
    },
  };

  const shareOnPlatform = useCallback(
    (platform: string, e: React.FormEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const url = encodeURIComponent(
        `${process.env.NEXT_PUBLIC_API_URL_APP_URL}/dl?ref_code=${userData?.ref_code}`,
      );

      let shareUrl = '';

      switch (platform) {
        case 'linkedin':
          shareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${url}`;
          break;
        case 'whatsapp':
          shareUrl = isMobile
            ? `whatsapp://send?text=${url}`
            : `https://web.whatsapp.com/send?text=${url}`;
          break;
        case 'twitter':
          shareUrl = `https://twitter.com/intent/tweet?url=${url}`;
          break;
        case 'facebook':
          shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
          break;
        case 'instagram':
          alert('Instagram sharing is only available via the mobile app.');
          return;
        default:
          return;
      }

      window.open(shareUrl, '_blank');
    },
    [userData?.ref_code, isMobile],
  );

  const shareMenu = useMemo(
    () => (
      <Menu
        items={[
          {
            key: 'whatsapp',
            label: (
              <span
                className='flex gap-x-3 items-center py-1'
                onClick={(e) => shareOnPlatform('whatsapp', e)}
              >
                <WhatsAppIcon /> WhatsApp
              </span>
            ),
          },
          {
            key: 'facebook',
            label: (
              <div
                onClick={(e) => shareOnPlatform('facebook', e)}
                className='flex gap-x-3.5 items-center py-1'
              >
                <span className='bg-[#0866FF] p-1.5 rounded-full w-6  h-6 flex justify-center items-center'>
                  <Image
                    src={Facebook || '/placeholder.svg'}
                    alt='Facebook'
                    className='w-3.5 h-3.5'
                  />{' '}
                </span>
                Facebook
              </div>
            ),
          },
          {
            key: 'linkedin',
            label: (
              <span
                className='flex gap-x-5 items-center py-1'
                onClick={(e) => shareOnPlatform('linkedin', e)}
              >
                <LinkedInIcon /> LinkedIn
              </span>
            ),
          },
        ]}
      />
    ),
    [shareOnPlatform],
  );

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const faqItems: FAQItem[] = useMemo(
    () => [
      {
        question: 'How does the Refer & Earn program work?',
        answer:
          'Share your unique referral link with friends. When they sign up and verify their email, you both earn 100 ZCoins (₹100) instantly.',
      },
      {
        question: 'What is a successful referral?',
        answer:
          'A referral is considered successful only when the invited user registers and verifies their email address.',
      },
      {
        question: 'How many people can I refer?',
        answer: `There's no limit! Refer as many friends as you like and keep earning ZCoins for each verified signup.`,
      },
      {
        question: 'Where can I use my ZCoins?',
        answer:
          'You can use ZCoins to get discounts on purchases. Earn 990 ZCoins and unlock 1 free eBook of your choice.',
      },
      {
        question: 'How can I track my referrals and earnings?',
        answer:
          'Go to the ‘Refer & Earn’ section in the app or website to see your referrals, verification status, and ZCoins earned.',
      },
      {
        question: 'Do my ZCoins expire?',
        answer: `No, referral ZCoins do not expire — you can accumulate and use them whenever you're ready to redeem rewards.`,
      },
    ],
    [],
  );

  const toggleAccordion = useCallback(
    (index: number) => {
      setActiveIndex(activeIndex === index ? -1 : index);
    },
    [activeIndex],
  );

  // Handle card click to show specific tables
  const handleCardClick = (cardId: number) => {
    if (cardId === 2) {
      setCurrentView('friendsJoined');
    } else if (cardId === 3) {
      setCurrentView('friendInvited');
    }
  };

  // Handle back button click to return to main view
  const handleBackClick = () => {
    setCurrentView('main');
  };

  // Add this useEffect near the top of your component
  useEffect(() => {
    // Set isMobile state based on user agent
    setIsMobile(/Mobi|Android/i.test(navigator.userAgent));

    // This ensures all client-side only code runs after hydration
    const timer = setTimeout(() => {
      setAnimationComplete((prev) => ({
        ...prev,
        clientReady: true,
      }));
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div>
      <Head>
        <title>{pageTitle('Refer & Earn')}</title>
      </Head>
      <div className='flex flex-col mt-10'>
        <div className='flex justify-center mx-4 md:mx-5 lg:mx-12 mt-8 sm:mt-16 pb-8 xl:pb-16 border-b border-cartBgColor'>
          <div className='flex flex-col w-full md:w-10/12 lg:w-8/12'>
            {/* Back button for table views */}
            {currentView !== 'main' && (
              <motion.div
                className='flex gap-x-2 items-center cursor-pointer group pb-6 md:pb-8'
                onClick={handleBackClick}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.button
                  className='z-10 w-8 h-8 p-2 rounded-full text-white flex justify-center items-center bg-cartBgColor'
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ChevronLeftIcon className='text-white w-4 h-4 xl:w-7 xl:h-7' />
                </motion.button>
                <span className='text-base font-medium group-hover:text-underline text-white'>
                  Back
                </span>
              </motion.div>
            )}

            {/* Conditional rendering based on current view */}
            <AnimatePresence mode='wait'>
              {currentView === 'main' ? (
                <motion.div
                  key='main-view'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <motion.div
                    ref={headerRef}
                    className='flex flex-col pb-3 md:pb-8'
                    initial='hidden'
                    animate={animationComplete.header ? 'visible' : 'hidden'}
                    variants={fadeInUp}
                  >
                    <div
                      onClick={() => router.push('/')}
                      className='flex gap-x-2 relative items-center cursor-pointer group  pb-6 md:pb-8'
                    >
                      <motion.button
                        className='z-10 w-8 h-8 p-2 absolute md:relative top-0 rounded-full text-white flex justify-center items-center bg-cartBgColor'
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <ChevronLeftIcon className='text-white w-4 h-4 xl:w-7 xl:h-7' />
                      </motion.button>
                      <ShouldRender check={screens.md}>
                        <motion.span
                          className='text-base font-medium group-hover:text-underline'
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.2 }}
                        >
                          Back To Home
                        </motion.span>
                      </ShouldRender>
                      <motion.span
                        className='rounded-full md:hidden w-full  justify-start items-center'
                        transition={{ delay: 1.5, duration: 0.5 }}
                      >
                        <Image
                          src={hands || '/placeholder.svg'}
                          alt='hands'
                          className='w-full max-h-[240px] text-[#C7C6CF]'
                        />{' '}
                      </motion.span>
                    </div>
                    <motion.h1
                      className='text-2xl sm:text-[32px] text-white font-semibold'
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      Invite Your Friends
                    </motion.h1>
                    <motion.h2
                      className='text-sm sm:text-base text-[#C7C6CF] font-[400] md:mt-2'
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      Guaranteed{' '}
                      <p className='font-semibold inline-flex'>
                        {coins} Z coins
                      </p>{' '}
                      on every user joins.
                    </motion.h2>
                  </motion.div>

                  <motion.span
                    ref={referralLinkRef}
                    className='flex flex-col sm:flex-row gap-2 md:gap-4 md:mt-4'
                    initial='hidden'
                    animate={
                      animationComplete.referralLink ? 'visible' : 'hidden'
                    }
                    variants={fadeInUp}
                    transition={{ delay: 0.5 }}
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.6 }}
                      className='w-full'
                    >
                      <Input
                        size='large'
                        disabled
                        suffix={
                          <Tooltip title={tooltipText} open={showTooltip}>
                            <motion.span
                              onClick={handleCopy}
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <CopyIcon />
                            </motion.span>
                          </Tooltip>
                        }
                        autoComplete='off'
                        value={`${process.env.NEXT_PUBLIC_API_URL_APP_URL}?ref_code=${userData?.ref_code}`}
                        placeholder={`${process.env.NEXT_PUBLIC_API_URL_APP_URL}?ref_code=${userData?.ref_code}`}
                        className='border text-base text-[#9F9EA6] cursor-pointer focus-within:bg-[#282829] placeholder:text-white rounded-lg border-[#282829] md:py-3 px-3 bg-[#282829]'
                      />
                    </motion.div>
                    <motion.div
                      className='min-w-200 flex justify-center items-center h-full'
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.7 }}
                    >
                      <Dropdown
                        menu={{ items: shareMenu.props.items }}
                        trigger={['click']}
                      >
                        <ButtonDefault
                          size={!screens.md ? 1 : 3}
                          variant={ButtonVariants.PRIMARY}
                          roundedMd={true}
                          className='rounded-lg w-full h-full max-h-[48px] min-w-200'
                        >
                          <motion.span
                            className='flex text-base font-semibold rounded-lg justify-center gap-x-2 py-2 px-6 whitespace-nowrap'
                            whileHover={{ scale: 1.02 }}
                          >
                            Invite via Referral Link
                          </motion.span>
                        </ButtonDefault>
                      </Dropdown>
                    </motion.div>
                  </motion.span>

                  <div className='text-white'>
                    <ShouldRender check={screens.md}>
                      <motion.div
                        ref={cardsWeb}
                        className='grid grid-cols-1 sm:grid-cols-2  gap-4 w-full py-8'
                        initial='hidden'
                      >
                        {dashboardCards.map((card, index) => (
                          <motion.div
                            key={`${card.id}-${index}`}
                            className={clsx(
                              index === 0 ? 'p-6' : '2xl:p-5 p-4',
                              'border border-[#3C3B3E] rounded-[12px] flex items-center justify-center hover:border-zinc-700 transition-colors cursor-pointer',
                            )}
                            variants={cardVariants}
                            whileHover='hover'
                            transition={{ delay: index * 0.1 + 0.8 }}
                            onClick={() => {
                              if (index === 0) router.push('/rewards/');
                              else handleCardClick(card.id);
                            }}
                          >
                            <div className='flex flex-col items-center gap-3 justify-center w-full'>
                              <div
                                className={clsx(
                                  index !== 0
                                    ? 'justify-start'
                                    : 'justify-center',
                                  'flex items-center justify-start w-full gap-x-2',
                                )}
                              >
                                <motion.span
                                  className='rounded-full flex justify-start items-center'
                                  initial={{ rotate: -10, opacity: 0 }}
                                  animate={{ rotate: 0, opacity: 1 }}
                                  transition={{ delay: index * 0.1 + 0.9 }}
                                >
                                  <Image
                                    src={card.icon || '/placeholder.svg'}
                                    alt='Icon'
                                    className='w-5 h-5 xl:w-6 xl:h-6 text-[#C7C6CF]'
                                  />{' '}
                                </motion.span>
                                <motion.span
                                  className={clsx(
                                    index === 0
                                      ? 'xl:text-sm tracking-wider'
                                      : 'text-base',
                                    `font-bold ${card.textColor} mt-1`,
                                  )}
                                  initial={{ x: -5, opacity: 0 }}
                                  animate={{ x: 0, opacity: 1 }}
                                  transition={{ delay: index * 0.1 + 1 }}
                                >
                                  {index === 1
                                    ? data.length
                                    : index === 2
                                      ? invitedData?.length
                                      : card.title}
                                </motion.span>
                              </div>

                              <div className='flex justify-between w-full'>
                                {card.subtitle && (
                                  <motion.span
                                    className='text-[10px] 2xl:text-xs font-[400] text-[#C7C6CF]'
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: index * 0.1 + 1.1 }}
                                  >
                                    {card.subtitle}
                                  </motion.span>
                                )}
                                {card.showArrow && (
                                  <motion.div
                                    className='bg-[#282829] 2xl:w-6 2xl:h-6 w-5 h-5 flex justify-center items-center rounded-full'
                                    whileHover={{ x: 3 }}
                                    initial={{ x: -5, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ delay: index * 0.1 + 1.1 }}
                                  >
                                    <ChevronRightIcon className='2xl:w-4 2xl:h-4 w-3.5 h-3.5 text-white' />
                                  </motion.div>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </motion.div>
                    </ShouldRender>
                    <ShouldRender check={!screens.md}>
                      <div className='flex flex-col pt-8 pb-4 gap-y-2'>
                        <motion.div
                          ref={cardsRef}
                          className='grid grid-cols-2  gap-2 w-full '
                          variants={containerVariants}
                          initial='hidden'
                          animate={animationComplete.cards ? 'show' : 'hidden'}
                        >
                          {dashboardCards?.slice(1).map((card, index) => (
                            <motion.div
                              key={`${card.id}-${index}`}
                              className={clsx(
                                'p-3',
                                'border border-[#3C3B3E] rounded-[12px] flex items-center justify-center hover:border-zinc-700 transition-colors cursor-pointer',
                              )}
                              variants={cardVariants}
                              whileHover='hover'
                              transition={{ delay: index * 0.1 + 0.8 }}
                              onClick={() => handleCardClick(card.id)}
                            >
                              <div className='flex flex-col items-center gap-1 justify-center w-full'>
                                <div
                                  className={clsx(
                                    'flex  justify-start items-center w-full gap-x-2',
                                  )}
                                >
                                  <motion.span
                                    className='rounded-full flex justify-start items-center'
                                    initial={{ rotate: -10, opacity: 0 }}
                                    animate={{ rotate: 0, opacity: 1 }}
                                    transition={{ delay: index * 0.1 + 0.9 }}
                                  >
                                    <Image
                                      src={card.icon || '/placeholder.svg'}
                                      alt='Icon'
                                      className='w-5 h-5 xl:w-6 xl:h-6 text-[#C7C6CF]'
                                    />{' '}
                                  </motion.span>
                                  <motion.span
                                    className={clsx(
                                      'text-base',
                                      `font-bold ${card.textColor} mt-1`,
                                    )}
                                    initial={{ x: -5, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ delay: index * 0.1 + 1 }}
                                  >
                                    {index === 0
                                      ? data.length
                                      : index === 1
                                        ? invitedData?.length
                                        : card.title}
                                  </motion.span>
                                </div>

                                <div className='flex justify-between w-full'>
                                  {card.subtitle && (
                                    <motion.span
                                      className='text-[10px] 2xl:text-xs font-[400] text-[#C7C6CF]'
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      transition={{ delay: index * 0.1 + 1.1 }}
                                    >
                                      {card.subtitle}
                                    </motion.span>
                                  )}
                                  {card.showArrow && (
                                    <motion.div
                                      className='bg-[#282829] 2xl:w-6 2xl:h-6 w-5 h-5 flex justify-center items-center rounded-full'
                                      whileHover={{ x: 3 }}
                                      initial={{ x: -5, opacity: 0 }}
                                      animate={{ x: 0, opacity: 1 }}
                                      transition={{ delay: index * 0.1 + 1.1 }}
                                    >
                                      <ChevronRightIcon className='2xl:w-4 2xl:h-4 w-3.5 h-3.5 text-white' />
                                    </motion.div>
                                  )}
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </motion.div>
                        <motion.div
                          className={clsx(
                            'p-3',
                            'border border-[#3C3B3E] rounded-[12px] flex items-center justify-center hover:border-zinc-700 transition-colors cursor-pointer',
                          )}
                          variants={cardVariants}
                          whileHover='hover'
                          transition={{ delay: 2 * 0.1 + 0.8 }}
                          onClick={() =>
                            dashboardCards[0]?.id &&
                            handleCardClick(dashboardCards[0].id)
                          }
                        >
                          <div className='flex flex-col items-center gap-1 justify-center w-full'>
                            <div
                              className={clsx(
                                'flex  justify-center items-center w-full gap-x-2',
                              )}
                            >
                              <motion.span
                                className='rounded-full flex justify-start items-center'
                                initial={{ rotate: -10, opacity: 0 }}
                                animate={{ rotate: 0, opacity: 1 }}
                                transition={{ delay: 2 * 0.1 + 0.9 }}
                              >
                                <Image
                                  src={
                                    (dashboardCards[0]?.icon &&
                                      dashboardCards[0].icon) ||
                                    '/placeholder.svg' ||
                                    '/placeholder.svg'
                                  }
                                  alt='Icon'
                                  className='w-5 h-5 xl:w-6 xl:h-6 text-[#C7C6CF]'
                                />{' '}
                              </motion.span>
                              <motion.span
                                className={clsx(
                                  'text-sm',
                                  `font-bold ${dashboardCards[0]?.textColor && dashboardCards[0].textColor} mt-1`,
                                )}
                                initial={{ x: -5, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 2 * 0.1 + 1 }}
                              >
                                {dashboardCards[0]?.title &&
                                  dashboardCards[0].title}
                              </motion.span>
                            </div>
                          </div>
                        </motion.div>
                      </div>
                    </ShouldRender>

                    <div className='flex flex-col lg:flex-row gap-4 2xl:gap-x-6'>
                      <motion.div
                        ref={howItWorksRef}
                        className='border border-[#3C3B3E] bg-[#151515] md:bg-transparent border-dashed w-full rounded-[12px] h-fit p-4 sm:p-6'
                        initial={{ opacity: 0, y: 30 }}
                        animate={
                          animationComplete.howItWorks
                            ? { opacity: 1, y: 0 }
                            : { opacity: 0, y: 30 }
                        }
                        transition={{ duration: 0.5 }}
                      >
                        <h2 className='text-white text-base font-semibold mb-4 md:mb-6'>
                          How referral works?
                        </h2>

                        <div className='grid grid-cols-1 gap-6'>
                          <motion.div
                            variants={staggerContainer}
                            initial='hidden'
                            animate={
                              animationComplete.howItWorks
                                ? 'visible'
                                : 'hidden'
                            }
                            className='space-y-6'
                          >
                            <div className='flex flex-col space-y-6'>
                              <AnimatePresence>
                                {REFERRAL_STEPS.map((step, index) => (
                                  <motion.div
                                    key={`${step.text}-${index}`}
                                    className={clsx(
                                      step.icon === 'coin'
                                        ? 'items-start'
                                        : 'items-center',
                                      'flex gap-3 sm:gap-4',
                                    )}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.15 + 1.2 }}
                                  >
                                    <ReferralStepIcon icon={step.icon} />
                                    <div className='flex flex-col'>
                                      <div className='text-white text-xs sm:text-sm'>
                                        {step.text}
                                      </div>
                                      <ShouldRender
                                        check={step.icon === 'coin'}
                                      >
                                        <motion.div
                                          initial={{ opacity: 0, y: 10 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          transition={{
                                            delay: index * 0.15 + 1.3,
                                          }}
                                        >
                                          <Button
                                            onClick={() =>
                                              router.push('/rewards')
                                            }
                                            className='bg-white text-black w-fit font-semibold text-xs rounded-[8px] max-h-[32px] mt-3'
                                          >
                                            <div className='font-medium'>
                                              Get Products for free
                                            </div>
                                          </Button>
                                        </motion.div>
                                      </ShouldRender>
                                    </div>
                                  </motion.div>
                                ))}
                              </AnimatePresence>
                            </div>
                          </motion.div>
                        </div>
                      </motion.div>

                      <motion.span
                        className='rounded-full hidden w-full md:flex justify-start items-center'
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={
                          animationComplete.howItWorks
                            ? { opacity: 1, scale: 1 }
                            : { opacity: 0, scale: 0.9 }
                        }
                        transition={{ delay: 1.5, duration: 0.5 }}
                      >
                        <Image
                          src={hands || '/placeholder.svg'}
                          alt='hands'
                          className='w-full max-h-[240px] text-[#C7C6CF]'
                        />{' '}
                      </motion.span>
                    </div>

                    <div className='w-full pt-4 md:pt-8'></div>

                    <motion.div
                      ref={faqRef}
                      className='w-full'
                      initial={{ opacity: 0, y: 30 }}
                      animate={
                        animationComplete.faq
                          ? { opacity: 1, y: 0 }
                          : { opacity: 0, y: 30 }
                      }
                      transition={{ duration: 0.5 }}
                    >
                      <motion.h2
                        className='text-base md:text-[24px] py-4 md:py-6 font-bold text-white'
                        initial={{ opacity: 0, y: 10 }}
                        animate={
                          animationComplete.faq
                            ? { opacity: 1, y: 0 }
                            : { opacity: 0, y: 10 }
                        }
                        transition={{ delay: 0.1 }}
                      >
                        Frequently Asked Questions
                      </motion.h2>

                      <div className='space-y-3 sm:space-y-4'>
                        {faqItems.map((item, index) => (
                          <motion.div
                            key={`${item.answer}-${index}`}
                            className={clsx(
                              activeIndex === index
                                ? 'bg-[#282829]'
                                : 'bg-[#1C1C1C] ',
                              'rounded-[10px] overflow-hidden',
                            )}
                            initial={{ opacity: 0, y: 20 }}
                            animate={
                              animationComplete.faq
                                ? { opacity: 1, y: 0 }
                                : { opacity: 0, y: 20 }
                            }
                            transition={{ delay: index * 0.1 + 0.2 }}
                          >
                            <motion.button
                              className='w-full flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 text-left text-white'
                              onClick={() => toggleAccordion(index)}
                              whileHover={{
                                backgroundColor: 'rgba(40, 40, 41, 0.5)',
                              }}
                            >
                              <span className='text-sm md:text-base font-medium'>
                                {item.question}
                              </span>
                              <motion.div
                                animate={{
                                  rotate: activeIndex === index ? 45 : 0,
                                }}
                                transition={{ duration: 0.2 }}
                              >
                                <PlusIcon className='h-4 w-4 sm:h-5 sm:w-5 text-[#FFFFFF99]' />
                              </motion.div>
                            </motion.button>

                            <AnimatePresence>
                              {activeIndex === index && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{
                                    duration: 0.3,
                                    ease: 'easeInOut',
                                  }}
                                  className='overflow-hidden'
                                >
                                  <div className='px-4 sm:px-6 py-4 sm:py-5 pt-0 text-xs sm:text-sm text-[#C7C6CF]'>
                                    {item.answer}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                    <div className='flex justify-center pt-6 2xl:pt-8  pb-8 2xl:pb-10'>
                      <Button
                        type='default'
                        onClick={() => router.push('/contact/')}
                        size='large'
                        className='border border-[#282829] text-white hover:border-gray-500 bg-transparent text-base rounded-[8px] py-3 px-8'
                      >
                        Contact Support
                      </Button>
                    </div>

                    <motion.div
                      ref={feedbackRef}
                      className=''
                      initial={{ opacity: 0, y: 20 }}
                      animate={
                        animationComplete.feedback
                          ? { opacity: 1, y: 0 }
                          : { opacity: 0, y: 20 }
                      }
                      transition={{ duration: 0.5, delay: 0.8 }}
                    >
                      <div className=''>
                        <div className='relative'>
                          <textarea
                            rows={3}
                            value={feedbackInput}
                            placeholder='How do you feel about our referral program?'
                            onChange={(e) => setFeedbackInput(e.target.value)}
                            className='w-full bg-transparent rounded-2xl p-4 placeholder:text-[#9F9EA6] pb-16 text-white focus:outline-none resize-none border border-[#282829]'
                          />
                          <div className='absolute bottom-4 right-4'>
                            <Button
                              onClick={handleSubmit}
                              disabled={!feedbackInput}
                              loading={loading}
                              className='disabled:bg-[#282829] bg-white border-none disabled:text-[#77777C] text-black font-medium rounded-lg px-4 py-2 text-sm  transition-colors'
                            >
                              Submit Feedback
                            </Button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              ) : currentView === 'friendsJoined' ? (
                <motion.div
                  key='friends-joined-view'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className='w-full'
                >
                  <FriendsJoinedTable
                    data={data}
                    loading={loadingFriendsTable}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key='friend-invited-view'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className='w-full'
                >
                  <FriendInvited
                    data={invitedData}
                    loading={loadingInvitedTable}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferEarn;
